import PreparationSourceAttribution from './PreparationSourceAttribution';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, CircularProgress, Divider, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DescriptionRounded as BriefIcon } from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import {
  buildJourneyBrief,
  hasJourneyBriefChanged,
  isJourneyBriefFinalizable,
} from './journeyBriefModel';
import useJourneyFinalizedRevisions from './useJourneyFinalizedRevisions';
import JourneyRevisionShareActions from './JourneyRevisionShareActions';
import AnswerDisplayText from './AnswerDisplayText';
import JourneyBriefHeader from './JourneyBriefHeader';
import { buildJourneyBriefHeader } from './journeyBriefHeaderModel';

const BriefStatement = ({ statement, section, accent, onOpenReferences, onEditAnswer }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(statement.text);
  const [saving, setSaving] = useState(false);

  useEffect(() => setText(statement.text), [statement.text]);

  const save = async () => {
    setSaving(true);
    const saved = await onEditAnswer?.(statement.questionId, text.trim());
    setSaving(false);
    if (saved) setEditing(false);
  };

  if (editing) {
    return (
      <Box>
        <TextField
          fullWidth multiline minRows={3} value={text}
          onChange={(event) => setText(event.target.value)}
          label={`Edit ${section.title}`}
        />
        <Stack direction="row" spacing={0.7} sx={{ mt: 0.6 }}>
          <Button disabled={saving || !text.trim()} onClick={save} sx={{ textTransform: 'none', fontWeight: 750 }}>Save</Button>
          <Button disabled={saving} onClick={() => { setText(statement.text); setEditing(false); }} sx={{ textTransform: 'none' }}>Cancel</Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack direction="row" spacing={0.6} alignItems="flex-start">
      <Box sx={{ flex: 1, minWidth: 0 }}>
      <AnswerDisplayText sx={{ flex: 1, minWidth: 0, fontSize: '0.9rem', lineHeight: 1.48, color: colors.semantic.ink }}>
        {statement.text}
      </AnswerDisplayText>
      <PreparationSourceAttribution authors={statement.sourceAuthors || []} />
      </Box>
      <Stack direction="row" spacing={0.35}>
        {onEditAnswer ? (
          <ButtonBase onClick={() => setEditing(true)} sx={{ px: 0.65, py: 0.3, borderRadius: '8px', color: accent.ink, fontSize: '0.7rem', fontWeight: 750 }}>
            Edit
          </ButtonBase>
        ) : null}
        {statement.sourceIds?.length ? (
          <ButtonBase
            onClick={() => onOpenReferences(statement.sourceIds)}
            aria-label={`View sources for ${section.title}`}
            sx={{ flexShrink: 0, px: 0.65, py: 0.3, borderRadius: '8px', color: accent.ink, bgcolor: alpha(accent.primary, 0.1), fontSize: '0.7rem', fontWeight: 750 }}
          >
            Sources
          </ButtonBase>
        ) : null}
      </Stack>
    </Stack>
  );
};

const BriefSections = ({ sections = [], accent, onOpenReferences, onEditAnswer }) => (
  <Stack spacing={1.5} divider={<Divider flexItem />}>
    {sections.map((section) => (
      <Box key={section.id}>
        <Typography sx={{ mb: 0.55, fontSize: '0.8rem', fontWeight: 750, color: alpha(colors.semantic.ink, 0.68) }}>
          {section.title}
        </Typography>
        <Stack spacing={0.7}>
          {(section.statements || []).map((statement) => (
            <BriefStatement
              key={statement.id}
              statement={statement}
              section={section}
              accent={accent}
              onOpenReferences={onOpenReferences}
              onEditAnswer={statement.origin === 'care_record_observation' ? undefined : onEditAnswer}
            />
          ))}
        </Stack>
      </Box>
    ))}
  </Stack>
);

const BriefFooter = ({ text }) => text ? (
  <Typography sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${alpha(colors.semantic.ink, 0.12)}`, fontSize: '0.68rem', lineHeight: 1.4, color: alpha(colors.semantic.ink, 0.52) }}>
    {text}
  </Typography>
) : null;

const JourneyBriefPanel = ({
  childId, ownerUserId, draftIdentity, snapshot, answers = [], confirmedPriorities = [], canEdit, accent, onOpenReferences,
  onRevisionStateChange, onEditAnswer, presentation = 'default', personName = '', preparedBy = '', briefHeader = null,
  onSaveBriefHeader,
}) => {
  const [confirming, setConfirming] = useState(false);
  const [previewing, setPreviewing] = useState(presentation !== 'custom');
  const reportHeader = useMemo(() => buildJourneyBriefHeader({
    snapshot, personName, preparedBy, overrides: briefHeader || {},
  }), [briefHeader, personName, preparedBy, snapshot]);
  const brief = useMemo(() => buildJourneyBrief({ snapshot, answers, confirmedPriorities, reportHeader }), [
    answers, confirmedPriorities, reportHeader, snapshot,
  ]);
  const canFinalize = isJourneyBriefFinalizable(brief);
  const revisionState = useJourneyFinalizedRevisions({ childId, ownerUserId, draftIdentity });
  const latestRevision = revisionState.latestRevision;
  const changedSinceFinalized = hasJourneyBriefChanged(brief, latestRevision);
  const showMutablePreview = canFinalize && (!latestRevision || changedSinceFinalized) && previewing;

  useEffect(() => {
    onRevisionStateChange?.({
      hasFinalizedRevision: Boolean(latestRevision),
      changedSinceFinalized,
    });
  }, [changedSinceFinalized, latestRevision, onRevisionStateChange]);

  const finish = async () => {
    const revision = await revisionState.finalize({ brief, answers });
    if (revision) setConfirming(false);
  };

  if (revisionState.loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={22} /></Box>;
  }

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: '24px', border: `1px solid ${alpha(accent.primary, 0.2)}` }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <BriefIcon sx={{ mt: 0.1, color: accent.ink }} />
        <Box sx={{ flex: 1 }}>
          <Typography component="h2" sx={{ fontSize: '1.15rem', fontWeight: 780, color: colors.semantic.ink }}>
            {latestRevision && !changedSinceFinalized ? 'Finished brief' : 'Preview your meeting brief'}
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: '0.8rem', lineHeight: 1.4, color: alpha(colors.semantic.ink, 0.62) }}>
            {latestRevision && changedSinceFinalized
              ? 'Your preparation has changed. The finished version below remains unchanged.'
              : 'Only answers you confirmed are included.'}
          </Typography>
        </Box>
      </Stack>

      {presentation === 'custom' && !latestRevision && !canFinalize ? (
        <Box sx={{ mt: 1.4 }}>
          <Button variant="contained" disabled sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 750 }}>
            Prepare brief
          </Button>
          <Typography sx={{ mt: 0.55, fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.62) }}>
            Choose or add at least one item to prepare your brief.
          </Typography>
        </Box>
      ) : null}

      {presentation === 'custom' && canFinalize && (!latestRevision || changedSinceFinalized) && !previewing ? (
        <Button variant="contained" onClick={() => setPreviewing(true)} sx={{ mt: 1.5, borderRadius: '999px', textTransform: 'none', fontWeight: 750 }}>
          {latestRevision ? 'Prepare newer brief' : 'Prepare brief'}
        </Button>
      ) : null}

      {showMutablePreview ? (
        <Box sx={{ mt: 1.5 }}>
          <JourneyBriefHeader
            header={brief.reportHeader}
            accent={accent}
            editable={canEdit && Boolean(onSaveBriefHeader)}
            draft
            onSave={onSaveBriefHeader}
          />
          <BriefSections
            sections={brief.sections}
            accent={accent}
            onOpenReferences={onOpenReferences}
            onEditAnswer={presentation === 'custom' && canEdit ? onEditAnswer : null}
          />
          <BriefFooter text={brief.reportHeader?.footerText} />
        </Box>
      ) : presentation !== 'custom' && !latestRevision ? (
        <Typography sx={{ mt: 1.4, fontSize: '0.86rem', lineHeight: 1.45, color: alpha(colors.semantic.ink, 0.68) }}>
          Confirm at least one preparation answer to finish a brief.
        </Typography>
      ) : null}

      {revisionState.revisions.length ? (
        <Stack spacing={1.2} sx={{ mt: 1.6 }}>
          {revisionState.revisions.map((revision, index) => (
            <Box key={revision.revisionId} sx={{ p: 1.2, borderRadius: '16px', bgcolor: alpha(accent.primary, 0.055) }}>
              <Typography sx={{ mb: 1, fontSize: '0.78rem', fontWeight: 780, color: accent.ink }}>
                {index === 0 ? 'Latest finished version' : `Earlier finished version ${revisionState.revisions.length - index}`}
              </Typography>
              {revision.recipientContent?.header || revision.reportHeader ? (
                <JourneyBriefHeader header={revision.recipientContent?.header || revision.reportHeader} accent={accent} />
              ) : null}
              <BriefSections sections={revision.sections || []} accent={accent} onOpenReferences={onOpenReferences} />
              <BriefFooter text={(revision.recipientContent?.header || revision.reportHeader)?.footerText} />
              <JourneyRevisionShareActions revision={revision} sharingAdapterId={snapshot.sharingAdapter} />
            </Box>
          ))}
        </Stack>
      ) : null}

      {revisionState.error ? <Alert severity="error" sx={{ mt: 1.2 }}>{revisionState.error}</Alert> : null}

      {canEdit && showMutablePreview ? (
        confirming ? (
          <Box sx={{ mt: 1.5, p: 1.2, borderRadius: '16px', border: `1px solid ${alpha(accent.primary, 0.2)}` }}>
            <Typography sx={{ fontSize: '0.84rem', lineHeight: 1.45, color: colors.semantic.ink }}>
              Finish this exact wording as a frozen version? Future changes will create a new version.
            </Typography>
            <Stack direction="row" spacing={0.8} sx={{ mt: 1 }}>
              <Button variant="contained" disabled={revisionState.finalizing} onClick={finish} sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 750 }}>
                {revisionState.finalizing ? 'Finishing…' : 'Finish and freeze'}
              </Button>
              <Button disabled={revisionState.finalizing} onClick={() => setConfirming(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                Cancel
              </Button>
            </Stack>
          </Box>
        ) : (
          <Button variant="contained" onClick={() => setConfirming(true)} sx={{ mt: 1.5, borderRadius: '999px', textTransform: 'none', fontWeight: 750 }}>
            {latestRevision ? 'Finish newer version' : 'Finish brief'}
          </Button>
        )
      ) : null}
    </Paper>
  );
};

export default JourneyBriefPanel;
